import { useEffect, useRef, useState } from "react";
import { animate, motion, useMotionValue } from "motion/react";
import MediaFrame from "./MediaFrame.jsx";
import { useMotionMode } from "../motion/MotionDirector.jsx";
import {
  MOTION_TIMING,
  editorialEase,
  projectIndexFromOffset
} from "../motion/choreography.js";

export default function ProjectRail({ projects }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [metrics, setMetrics] = useState({ maxDrag: 0, step: 1 });
  const viewportRef = useRef(null);
  const railRef = useRef(null);
  const x = useMotionValue(0);
  const { mode } = useMotionMode();

  useEffect(() => {
    const measure = () => {
      const viewport = viewportRef.current;
      const rail = railRef.current;
      if (!viewport || !rail) return;
      const maxDrag = Math.max(0, rail.scrollWidth - viewport.clientWidth);
      const step = projects.length > 1 ? maxDrag / (projects.length - 1) : 1;
      setMetrics({ maxDrag, step: Math.max(step, 1) });
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (viewportRef.current) observer.observe(viewportRef.current);
    if (railRef.current) observer.observe(railRef.current);
    return () => observer.disconnect();
  }, [projects.length]);

  const selectProject = (index) => {
    setActiveIndex(index);
    if (mode === "full") {
      animate(x, -metrics.step * index, {
        duration: MOTION_TIMING.projectDuration,
        ease: editorialEase
      });
    }
  };

  const settleAfterDrag = () => {
    const next = projectIndexFromOffset(x.get(), metrics.step, projects.length);
    selectProject(next);
  };

  return (
    <section id="projects" className="projects" aria-labelledby="projects-title">
      <div className="section-kicker section-kicker--light">
        <span>03 / Selected Experiments</span>
        <span>Drag / Select</span>
      </div>
      <div className="projects__heading">
        <h2 id="projects-title">PUBLIC<br />EXPERIMENTS</h2>
        <p>三个公开展出的正式装置。选择或拖动项目，查看创意方向、关键原型与跨学科交付。</p>
      </div>
      <motion.div
        className="projects__sun"
        animate={{
          x: `${activeIndex * 13}vw`,
          scale: 1 - activeIndex * 0.08,
          rotate: activeIndex * 18
        }}
        transition={{ duration: 0.82, ease: editorialEase }}
        aria-hidden="true"
      />
      <div className="projects__viewport" ref={viewportRef}>
        <motion.div
          className="projects__rail"
          ref={railRef}
          role="list"
          aria-label="代表项目"
          style={mode === "full" ? { x } : undefined}
          drag={mode === "full" ? "x" : false}
          dragConstraints={{ left: -metrics.maxDrag, right: 0 }}
          dragElastic={0.08}
          dragMomentum
          onDragEnd={settleAfterDrag}
        >
          {projects.map((project, index) => {
            const active = index === activeIndex;
            return (
              <motion.article
                className={`project ${active ? "is-active" : ""}`}
                key={project.id}
                role="listitem"
                animate={{
                  opacity: active ? 1 : 0.48,
                  y: active && mode === "full" ? -24 : 0,
                  scale: active ? 1 : 0.965
                }}
                transition={{ duration: 0.58, ease: editorialEase }}
              >
                <button
                  type="button"
                  className="project__select"
                  aria-pressed={active}
                  onClick={() => selectProject(index)}
                >
                  <span>Project {project.number}</span>
                  <span>{project.year}</span>
                </button>
                <div className="project__poster">
                  <MediaFrame
                    src={project.media.src}
                    poster={project.media.poster}
                    alt={project.media.alt}
                  />
                </div>
                <div className="project__copy">
                  <span className="project__number" aria-hidden="true">{project.number}</span>
                  <h3>
                    <span>{project.titleZh}</span>
                    <em>{project.titleEn}</em>
                  </h3>
                  <p>{project.description}</p>
                  <strong>{project.result}</strong>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
      <div className="projects__controls" aria-label="项目快速选择">
        {projects.map((project, index) => (
          <button
            type="button"
            key={project.id}
            aria-label={`选择项目 ${project.titleZh}`}
            aria-pressed={index === activeIndex}
            onClick={() => selectProject(index)}
          >
            {project.number}
          </button>
        ))}
      </div>
    </section>
  );
}
